import type {
  LocalProject,
  TextBlock,
  TranslationRequestBlock,
  TranslationResponseItem
} from "../types";

const TOKEN_KEY = "paper-bridge-session";
const TOKEN_EXPIRY_KEY = "paper-bridge-session-expiry";
const MAX_BLOCKS_PER_BATCH = 20;
const MAX_CHARACTERS_PER_BATCH = 10_000;

function apiBase(): string {
  return (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/+$/, "");
}

function parseError(payload: unknown, fallback: string): string {
  if (
    payload &&
    typeof payload === "object" &&
    "error" in payload &&
    typeof payload.error === "string"
  ) {
    return payload.error;
  }
  return fallback;
}

export function isApiConfigured(): boolean {
  return Boolean(apiBase());
}

export function hasValidSession(): boolean {
  const token = sessionStorage.getItem(TOKEN_KEY);
  const expiresAt = Number(sessionStorage.getItem(TOKEN_EXPIRY_KEY));
  return Boolean(token && Number.isFinite(expiresAt) && expiresAt > Date.now());
}

export function clearSession(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
}

export async function authenticate(passphrase: string): Promise<void> {
  if (!apiBase()) {
    throw new Error("尚未配置 VITE_API_BASE_URL，无法连接翻译服务。");
  }
  const response = await fetch(`${apiBase()}/api/auth`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ passphrase })
  });
  const payload = (await response.json().catch(() => null)) as {
    token?: unknown;
    expiresAt?: unknown;
  } | null;
  if (
    !response.ok ||
    !payload ||
    typeof payload.token !== "string" ||
    typeof payload.expiresAt !== "number"
  ) {
    throw new Error(parseError(payload, `登录失败（${response.status}）`));
  }
  sessionStorage.setItem(TOKEN_KEY, payload.token);
  sessionStorage.setItem(TOKEN_EXPIRY_KEY, String(payload.expiresAt));
}

export function buildDocumentContext(project: LocalProject): string {
  return project.layout
    .flatMap((page) => page.blocks)
    .filter(
      (block) =>
        block.role === "title" ||
        block.role === "heading" ||
        (block.pageNumber === 1 && block.role === "paragraph")
    )
    .slice(0, 16)
    .map((block) => block.text)
    .join("\n")
    .slice(0, 4_000);
}

export function createTranslationBatches(
  blocks: TextBlock[]
): TranslationRequestBlock[][] {
  const batches: TranslationRequestBlock[][] = [];
  let current: TranslationRequestBlock[] = [];
  let currentLength = 0;

  for (const block of blocks) {
    const requestBlock = {
      id: block.id,
      role: block.role,
      text: block.text
    };
    const wouldOverflow =
      current.length >= MAX_BLOCKS_PER_BATCH ||
      (current.length > 0 &&
        currentLength + block.text.length > MAX_CHARACTERS_PER_BATCH);
    if (wouldOverflow) {
      batches.push(current);
      current = [];
      currentLength = 0;
    }
    current.push(requestBlock);
    currentLength += block.text.length;
  }
  if (current.length) batches.push(current);
  return batches;
}

function wait(milliseconds: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(resolve, milliseconds);
    signal?.addEventListener(
      "abort",
      () => {
        window.clearTimeout(timeout);
        reject(new DOMException("Translation cancelled", "AbortError"));
      },
      { once: true }
    );
  });
}

export async function translateBatch(
  documentId: string,
  context: string,
  blocks: TranslationRequestBlock[],
  signal?: AbortSignal
): Promise<TranslationResponseItem[]> {
  const token = sessionStorage.getItem(TOKEN_KEY);
  if (!token || !hasValidSession()) {
    clearSession();
    throw new Error("登录已失效，请重新输入访问口令。");
  }

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const response = await fetch(`${apiBase()}/api/translate`, {
      method: "POST",
      signal,
      headers: {
        authorization: `Bearer ${token}`,
        "content-type": "application/json"
      },
      body: JSON.stringify({ documentId, context, blocks })
    });
    const payload = (await response.json().catch(() => null)) as {
      translations?: unknown;
      error?: unknown;
    } | null;

    if (response.status === 401) {
      clearSession();
      throw new Error(parseError(payload, "登录已失效，请重新登录。"));
    }
    if (response.status === 429 && attempt < 2) {
      await wait(1_500 * 2 ** attempt, signal);
      continue;
    }
    if (!response.ok) {
      throw new Error(
        parseError(payload, `翻译服务请求失败（${response.status}）`)
      );
    }
    if (!payload || !Array.isArray(payload.translations)) {
      throw new Error("翻译服务返回了无法识别的数据。");
    }

    const expectedIds = new Set(blocks.map((block) => block.id));
    const seen = new Set<string>();
    const translations = payload.translations.map((item) => {
      const candidate = item as { id?: unknown; text?: unknown };
      if (
        typeof candidate.id !== "string" ||
        typeof candidate.text !== "string" ||
        !expectedIds.has(candidate.id) ||
        seen.has(candidate.id)
      ) {
        throw new Error("翻译结果的文本块对应关系不完整。");
      }
      seen.add(candidate.id);
      return { id: candidate.id, text: candidate.text.trim() };
    });
    if (seen.size !== expectedIds.size) {
      throw new Error("翻译结果缺少部分文本块。");
    }
    return translations;
  }

  throw new Error("翻译服务暂时繁忙，请稍后重试。");
}
